/**
 * How wide the sidebar and the message list are, on the layout that has all
 * three columns.
 *
 * The stylesheet owns every narrower layout and this module owns the widest
 * one, because the widest one is the only place two columns have a fixed width
 * and an edge between them to take hold of. Below the three-column breakpoint
 * this removes what it set and gets out of the way: `styles.css` still carries
 * the values a phone and a tablet use, and a 560px list dragged on a desktop
 * would be most of a tablet.
 *
 * # A stored width has to survive a different screen
 *
 * The number in `localStorage` is chosen on one window and read back on every
 * window after it, which will not all be that size -- the same profile opens on
 * a 27" monitor and on a laptop, and a preference that made the app unusable on
 * the second one would be a preference worth not having. So a width is clamped
 * on the way *out*: against its own bounds, and then against what is left of the
 * viewport once whatever is elastic has kept {@link READER_MIN}.
 *
 * Loading a width never writes one. Reading a 560px list on a small laptop
 * narrows it for that session and leaves the stored 560 alone, so the monitor it
 * was chosen on still gets it when the lid opens there. Only a gesture stores
 * anything, and only a gesture that actually moved the column: a divider that
 * has no room left is one a press must leave alone, or a laptop would overwrite
 * the monitor's preference with the width it had squeezed it into.
 */

import { computed, effect, signal, type Signal } from "@preact/signals";

import { THREE_COLUMN } from "./navigation";
import { listWidth, readingPane, sidebarWidth } from "./settings";
import * as store from "./store";

/** The two column edges a person can move, named for the column to their left. */
export type Edge = "sidebar" | "list";

const EDGES = ["sidebar", "list"] as const;

/**
 * The narrowest each column may be.
 *
 * The sidebar's floor is a folder name with a count beside it; below about this
 * the tree is a column of ellipses.
 *
 * The list's floor is set by the rows rather than by the header: a subject
 * truncates gracefully at any width, but the tags underneath it -- the account
 * a message arrived at, the paperclip -- are laid out to fit rather than to
 * shrink, and below about 300px they run past the column edge. Both are well
 * under the 26rem at which the `listcol` container query drops the Filters
 * caption, so the header's narrow rendering is what anyone dragging inwards
 * meets long before either minimum.
 */
const SIDEBAR_MIN = 168;
const LIST_MIN = 300;

/**
 * The widest, before the column stops being what it is.
 *
 * A 420px sidebar holds the longest folder name anyone has; past that it is
 * padding. A list wider than 640px is a reading pane with worse typography, and
 * a person who wants that has a setting for it -- turning the reading pane off
 * gives the list the whole window.
 */
const SIDEBAR_MAX = 420;
const LIST_MAX = 640;

/**
 * What the elastic column keeps for itself, whatever the fixed ones ask for.
 *
 * 380px is not a guess: it is exactly what the reader gets from the default
 * three-column layout at the breakpoint itself (900 - 200 - 320), so the
 * defaults never trip the squeeze below and every pixel it takes back is one a
 * drag added. It guards the message list just as much as the reader, because
 * with the reading pane off the list is the column that stretches.
 */
const READER_MIN = 380;

const LIMITS: Record<Edge, { min: number; max: number }> = {
  sidebar: { min: SIDEBAR_MIN, max: SIDEBAR_MAX },
  list: { min: LIST_MIN, max: LIST_MAX },
};

/**
 * The widths nobody has chosen, which used to be a `@media (min-width: 75rem)`
 * block in `styles.css`.
 *
 * They moved here whole rather than being read back out of the stylesheet: a
 * width that comes from two places cannot be clamped in one, and the clamp is
 * the part that keeps the app usable. A window between 900 and 1200 CSS pixels
 * is a laptop with something else on screen beside it, and the reading pane is
 * what suffers if the two fixed columns keep their full width.
 */
const DEFAULTS: Record<"narrow" | "roomy", Record<Edge, number>> = {
  narrow: { sidebar: 200, list: 320 },
  roomy: { sidebar: 232, list: 380 },
};

const STORED: Record<Edge, Signal<number | null>> = {
  sidebar: sidebarWidth,
  list: listWidth,
};

/**
 * A width being dragged or nudged, before anything has been stored.
 *
 * `from` is the width the gesture started at, which is what says at the end of
 * it whether anything moved.
 */
const live = signal<{
  edge: Edge;
  px: number;
  from: number;
  by: "pointer" | "key";
} | null>(null);

function media(query: string): Signal<boolean> {
  const mq = window.matchMedia(query);
  const matches = signal(mq.matches);
  mq.addEventListener("change", (e) => (matches.value = e.matches));
  return matches;
}

/** Whether the sidebar, the list and the reader are all on screen at once. */
const threeColumn = media(THREE_COLUMN);

const roomy = media("(min-width: 75rem)");

const viewport = signal(window.innerWidth);
// A signal set to the value it already holds notifies nothing, so a resize that
// only changes the height costs one comparison.
window.addEventListener("resize", () => (viewport.value = window.innerWidth));

/**
 * True when the reading pane is a column of its own.
 *
 * Both halves matter to the arithmetic below: the setting decides whether there
 * is a third column at all, and the view decides whether the second and third
 * have been replaced by a panel that spans them. In either of those cases the
 * list has no width of its own -- it is elastic, or it is not on screen -- and
 * subtracting a stored one would shrink the sidebar for a column that is not
 * there. It is also what `app.tsx` renders the layout from, so the class on the
 * shell and the arithmetic here cannot disagree about it.
 */
export const readerColumn = computed(
  () => readingPane.value === "side" && store.view.value === "mail",
);

/** The widths in effect right now, clamped and rounded to whole pixels. */
export const widths = computed<Record<Edge, number>>(() => {
  const fallback = roomy.value ? DEFAULTS.roomy : DEFAULTS.narrow;
  const drag = live.value;
  const w = {} as Record<Edge, number>;

  for (const edge of EDGES) {
    const raw = drag?.edge === edge ? drag.px : STORED[edge].value;
    // `localStorage` is a text file anyone can edit, and an old build or a
    // fat-fingered devtools session can leave a string in one of these keys.
    // `NaNpx` in `grid-template-columns` invalidates the whole declaration, so
    // the three columns would collapse into one with nothing on screen to say
    // why.
    const px = typeof raw === "number" && Number.isFinite(raw) ? raw : fallback[edge];
    w[edge] = Math.round(clamp(px, LIMITS[edge].min, LIMITS[edge].max));
  }

  const fixed = w.sidebar + (readerColumn.value ? w.list : 0);
  let over = fixed - (viewport.value - READER_MIN);
  if (over > 0) {
    // Who gives way, in order. The column being dragged goes first, so that
    // running out of room stops the divider under the pointer rather than
    // shoving the other one along ahead of it. Failing that -- and when nothing
    // is being dragged at all, which is a stored width meeting a smaller window
    // -- the sidebar gives before the list, because a folder tree at its floor
    // is still a folder tree while a message row that has lost the sender is no
    // longer worth scanning.
    //
    // It has to fall through to the other column rather than stop at the one
    // being dragged: a list stored wider than this window fits is already
    // borrowing from the sidebar, and refusing to go on borrowing while the
    // sidebar is dragged would spring the list back to its stored width the
    // moment the divider was touched.
    const order = drag ? [drag.edge, ...EDGES.filter((e) => e !== drag.edge)] : EDGES;
    for (const edge of order) {
      if (over <= 0) break;
      if (edge === "list" && !readerColumn.value) continue;
      const give = Math.min(over, w[edge] - LIMITS[edge].min);
      w[edge] -= give;
      over -= give;
    }
  }

  return w;
});

/**
 * How far each edge may travel on this window, for the separator to report.
 *
 * The maximum is the live one rather than the constant: it is the answer to
 * "how far can I drag this", and on a 1000px window that is a smaller number
 * than {@link SIDEBAR_MAX}. A screen reader reading out a range the divider
 * would refuse is the accessible version of a stuck control.
 */
export const bounds = computed<Record<Edge, { min: number; max: number }>>(() => {
  const w = widths.value;
  const spare = viewport.value - READER_MIN;
  const cap = (edge: Edge, taken: number) =>
    Math.max(LIMITS[edge].min, Math.min(LIMITS[edge].max, spare - taken));
  return {
    sidebar: { min: SIDEBAR_MIN, max: cap("sidebar", readerColumn.value ? w.list : 0) },
    list: { min: LIST_MIN, max: cap("list", w.sidebar) },
  };
});

/** Move an edge with the pointer. Stores nothing; {@link endDrag} does that. */
export function dragTo(edge: Edge, px: number): void {
  push(edge, px, "pointer");
}

function push(edge: Edge, px: number, by: "pointer" | "key"): void {
  clearTimeout(settle);
  const current = live.peek();
  // Where the gesture began, kept across every move within it: the pointer's
  // own start position would do for a drag, but a run of arrow keys has no
  // equivalent and both have to answer the same question at the end.
  const from = current?.edge === edge ? current.from : widths.peek()[edge];
  live.value = { edge, px, from, by };
}

/**
 * How long the keyboard waits before it writes.
 *
 * A held arrow key repeats about thirty times a second and every repeat would
 * otherwise be a `localStorage` write, which is the same objection as writing
 * once per frame of a drag. Short enough that letting go and closing the tab
 * keeps the width, long enough that a run of repeats is one write.
 */
const KEY_SETTLE = 400;

let settle: ReturnType<typeof setTimeout> | undefined;

/** Move an edge with the keyboard, storing it once the keys stop. */
export function keyTo(edge: Edge, px: number): void {
  push(edge, px, "key");
  settle = setTimeout(commit, KEY_SETTLE);
}

/** Finish a pointer drag, storing where it ended. */
export function endDrag(): void {
  clearTimeout(settle);
  commit();
}

function commit(): void {
  const drag = live.peek();
  if (!drag) return;
  // Read before the gesture is cleared: what is stored is the width that was on
  // screen at the end of it, which is the clamped one and not the raw pointer
  // position -- nothing is stored that this window has already refused to draw.
  const now = widths.peek()[drag.edge];
  live.value = null;
  // A gesture that moved nothing stores nothing. Two things depend on it: a
  // press and release on a divider must not freeze the width the window chose
  // into a preference that then stops following it, and a divider with no room
  // left on a small screen must not answer a hopeful tug by overwriting the
  // width that came from a bigger one.
  if (now !== drag.from) STORED[drag.edge].value = now;
}

/** Forget a chosen width, which puts the column back on the stylesheet's own. */
export function reset(edge: Edge): void {
  clearTimeout(settle);
  live.value = null;
  STORED[edge].value = null;
}

function clamp(px: number, min: number, max: number): number {
  return Math.min(Math.max(px, min), max);
}

/*
 * Applied at import, not from a component.
 *
 * The properties live on the root element, which exists before this module is
 * parsed, so there is nothing in the tree to wait for -- and waiting for the
 * first render would mean painting the default widths and then replacing them,
 * which is a visible jump on every load for anyone who has dragged anything.
 */
effect(() => {
  const style = document.documentElement.style;
  if (!threeColumn.value) {
    style.removeProperty("--sidebar-w");
    style.removeProperty("--list-w");
    return;
  }
  const w = widths.value;
  style.setProperty("--sidebar-w", `${w.sidebar}px`);
  style.setProperty("--list-w", `${w.list}px`);
});

// The resize cursor and the selection block belong to the whole document while a
// drag is running: the pointer is captured, so it spends the drag over the
// columns either side of the divider, and each of those would otherwise put its
// own cursor up and start selecting text under it. Keyboard nudges are excluded
// -- there is no pointer to draw a cursor for.
effect(() => {
  document.documentElement.classList.toggle("resizing", live.value?.by === "pointer");
});
